import React from 'react';
import Select, { components, InputProps } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { ChevronDown, X } from 'lucide-react';
import { DropdownLayoutProps, Option, OptionsGroup } from 'ui-builder';
import { cn } from 'shadcn-ui';
import ShadcnBaseInputLayout from '../../layout/ShadcnBaseInputLayout';

function AutoCompleteOffInput({ ...rest }: InputProps) {
  return <components.Input {...rest} autoComplete="nope" />;
}

const isCompact = (theme?: string) => theme === 'compact';

function getClassNames(theme?: string) {
  if (isCompact(theme)) {
    return {
      control: (state: { isFocused: boolean; isDisabled: boolean }) =>
        cn(
          'bg-transparent border-0 border-b border-dashed border-gray-400 rounded-none h-auto py-0 px-0 text-sm cursor-pointer inline-flex min-h-0 outline-none',
          state.isFocused && 'border-gray-600',
          state.isDisabled && 'opacity-50 cursor-not-allowed',
        ),
      placeholder: () => 'text-input-placeholder truncate text-sm',
      singleValue: () => 'text-gray-700 text-sm truncate',
      menu: () =>
        'bg-popover text-popover-foreground mt-1 rounded-md border border-gray-200 shadow-md z-50 overflow-hidden',
      menuList: () => 'p-1 max-h-60 overflow-y-auto',
      option: (state: {
        isFocused: boolean;
        isSelected: boolean;
        isDisabled: boolean;
      }) =>
        cn(
          'relative flex w-full cursor-default items-center rounded-sm py-2 px-3 text-sm select-none',
          state.isFocused && 'bg-gray-100 text-gray-700',
          state.isSelected && 'font-medium',
          state.isDisabled && 'pointer-events-none opacity-50',
        ),
      clearIndicator: () =>
        'text-gray-400 hover:text-gray-600 cursor-pointer p-0',
      dropdownIndicator: () => 'hidden',
      indicatorsContainer: () => 'hidden',
      groupHeading: () => 'text-muted-foreground px-2 py-1.5 text-xs font-medium',
      input: () =>
        'text-sm text-gray-700 w-0 absolute [&_input]:!border-none [&_input]:!outline-none [&_input]:!shadow-none [&_input]:!h-auto [&_input]:!p-0',
      valueContainer: () => 'gap-1 px-0 truncate',
      noOptionsMessage: () => 'text-sm text-gray-400 py-2 px-3',
    };
  }

  return {
    control: (state: { isFocused: boolean; isDisabled: boolean }) =>
      cn(
        '!h-9 w-full min-w-0 !rounded-full !border !border-gray-200 !bg-gray-100 !pl-3 !pr-2 !py-0 !text-sm !text-gray-700 !shadow-none !outline-none flex items-center cursor-pointer',
        state.isFocused && '!border-blue-500',
        state.isDisabled && 'opacity-50 cursor-not-allowed',
      ),
    placeholder: () => 'text-input-placeholder truncate text-sm',
    singleValue: () => 'text-gray-700 text-sm truncate',
    menu: () =>
      'bg-popover text-popover-foreground mt-1 rounded-md border border-gray-200 shadow-md z-50 overflow-hidden',
    menuList: () => 'p-1 max-h-60 overflow-y-auto',
    option: (state: {
      isFocused: boolean;
      isSelected: boolean;
      isDisabled: boolean;
    }) =>
      cn(
        'relative flex w-full cursor-default items-center rounded-sm py-2 px-3 text-sm select-none',
        state.isFocused && 'bg-gray-100 text-gray-700',
        state.isSelected && 'font-medium',
        state.isDisabled && 'pointer-events-none opacity-50',
      ),
    clearIndicator: () =>
      'text-gray-400 hover:text-gray-600 cursor-pointer p-0',
    dropdownIndicator: () => 'text-gray-400 p-0 pr-1',
    indicatorsContainer: () => 'gap-1',
    groupHeading: () => 'text-muted-foreground px-2 py-1.5 text-xs font-medium',
    input: () =>
      'text-sm text-gray-700 [&_input]:!border-none [&_input]:!outline-none [&_input]:!shadow-none [&_input]:!h-auto [&_input]:!p-0 [&_input]:!caret-transparent',
    valueContainer: () => 'gap-1 px-0 truncate',
    noOptionsMessage: () => 'text-sm text-gray-400 py-2 px-3',
  };
}

function ShadcnDropdownLayout(props: DropdownLayoutProps) {
  const areOptionsGrouped = () => {
    return (
      props.options &&
      props.options?.length > 0 &&
      Array.isArray(props.options[0].options)
    );
  };

  const getCurrentValue = () => {
    if (areOptionsGrouped()) {
      let result;
      (props.options as OptionsGroup[]).forEach((group) => {
        group.options.forEach((option) => {
          if (option.value === props.value) {
            result = option;
          }
        });
      });
      return result;
    } else {
      return (
        (props.options as Option[]).find(
          (option) => option.value === props.value,
        ) || undefined
      );
    }
  };

  const isClearable =
    props.isClearable !== undefined ? props.isClearable : true;

  const Widget = props.optionCreateFunc ? CreatableSelect : Select;
  const classNames = getClassNames(props.theme);

  return (
    <ShadcnBaseInputLayout
      name={props.name}
      errors={props.errors}
      hint={props.hint}
      label={props.label}
      className={props.className}
      hideLabel={props.hideLabel}
      showRequiredIcon={props.showRequiredIcon}
    >
      <Widget
        name={props.name}
        unstyled
        classNames={{ ...classNames, menuPortal: () => 'nekst-dropdown-portal' }}
        menuPlacement="auto"
        menuPortalTarget={document.body}
        styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999, pointerEvents: 'auto' as const }) }}
        options={props.options}
        isClearable={isClearable}
        value={getCurrentValue() || ''}
        placeholder={props.placeholder}
        isDisabled={props.isDisabled}
        onChange={(newValue: any) => {
          props.onChangeCallback({
            target: {
              value: newValue ? newValue.value : null,
            },
          });
        }}
        onCreateOption={props.optionCreateFunc}
        onInputChange={(newValue) => props.onInputChange?.(newValue)}
        components={{
          IndicatorSeparator: () => null,
          // @ts-expect-error react-select Input type mismatch with autoComplete prop
          Input: AutoCompleteOffInput,
          DropdownIndicator: (indicatorProps) => (
            <components.DropdownIndicator {...indicatorProps}>
              <ChevronDown className="size-4 opacity-50" />
            </components.DropdownIndicator>
          ),
          ClearIndicator: (clearProps) => (
            <components.ClearIndicator {...clearProps}>
              <X className="size-3.5" />
            </components.ClearIndicator>
          ),
        }}
        formatOptionLabel={(data) => {
          return data.labelWidget || data.label;
        }}
        data-testid={`${props.name}-dropdown`}
        data-test={`${props.name}-dropdown`}
      />
    </ShadcnBaseInputLayout>
  );
}

export default ShadcnDropdownLayout;
